<script setup>
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AssistantAPI from 'dashboard/api/teletel/assistant';
import ToolsAPI from 'dashboard/api/teletel/tools';
import AiUsageAPI from 'dashboard/api/teletel/aiUsage';
import Button from 'dashboard/components-next/button/Button.vue';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import ContentCard from 'dashboard/components-next/teletel/ContentCard.vue';

const { isTeletelFeatureEnabled } = useTeletelFeatures();

const { t } = useI18n();

const TABS = [
  'bots',
  'faqs',
  'documents',
  'scenarios',
  'inboxes',
  'playground',
  'performance',
  'tools',
  'usage',
];
const activeTab = ref('bots');
const available = computed(() => Boolean(isTeletelFeatureEnabled('assistant')));

const bots = ref([]);
const aiAllowed = ref(true);
const loading = ref(false);
const selectedId = ref(null);
// "New assistant" is a mode, not a missing selection: the list stays visible
// and the previous choice comes back on cancel.
const creating = ref(false);
const selectedBot = computed(() =>
  bots.value.find(b => b.id === selectedId.value)
);

// Which assistant opens by default. The page used to take the first by name,
// which on an account with old test assistants was always one of those - the
// owner saved a real assistant and landed on «22». Now: the one opened last
// on this browser, else the most recently updated.
const route = useRoute();
const LAST_BOT_KEY = `teletel_assistant_last_bot:${route.params.accountId}`;
const rememberedBotId = () => {
  try {
    return Number(localStorage.getItem(LAST_BOT_KEY)) || null;
  } catch (e) {
    return null;
  }
};
const rememberBot = id => {
  try {
    if (id) localStorage.setItem(LAST_BOT_KEY, String(id));
  } catch (e) {
    // private mode or blocked storage: the default still works
  }
};
const preferredBotId = list => {
  const remembered = rememberedBotId();
  if (remembered && list.some(b => b.id === remembered)) return remembered;
  const newest = [...list].sort((a, b) =>
    String(b.updated_at || '').localeCompare(String(a.updated_at || ''))
  )[0];
  return newest ? newest.id : null;
};

// The assistants shown as cards, newest change first, so the one being
// worked on is at the top and two with the same name stay distinguishable.
const sortedBots = computed(() =>
  [...bots.value].sort((a, b) =>
    String(b.updated_at || '').localeCompare(String(a.updated_at || ''))
  )
);

// Would this assistant answer a customer today? It needs approved knowledge
// (it stays silent without it, by rule) and a linked inbox.
const readiness = b => {
  const knows = b.faqs_count > 0;
  const linked = b.inbox_ids.length > 0;
  if (knows && linked) return 'READY';
  if (linked) return 'LINKED_NO_KNOWLEDGE';
  if (knows) return 'KNOWLEDGE_NOT_LINKED';
  return 'EMPTY';
};
const readinessClass = b =>
  ({
    READY: 'bg-n-teal-3 text-n-teal-11',
    LINKED_NO_KNOWLEDGE: 'bg-n-amber-3 text-n-amber-11',
    KNOWLEDGE_NOT_LINKED: 'bg-n-blue-3 text-n-blue-11',
    EMPTY: 'bg-n-alpha-2 text-n-slate-11',
  })[readiness(b)];
const readinessIcon = b =>
  ({
    READY: 'i-lucide-check-circle-2',
    LINKED_NO_KNOWLEDGE: 'i-lucide-alert-triangle',
    KNOWLEDGE_NOT_LINKED: 'i-lucide-plug-zap',
    EMPTY: 'i-lucide-circle-dashed',
  })[readiness(b)];

const formatUpdated = iso => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('ar', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch (e) {
    return iso;
  }
};

// Tools belong to the account, not to one assistant: any assistant may use them.
const tools = ref([]);
const toolSaving = ref(false);
const toolTesting = ref(null);
const toolResult = reactive({ id: null, ok: null, text: '' });

const blankTool = () => ({
  id: null,
  title: '',
  description: '',
  endpoint_url: '',
  http_method: 'GET',
  request_template: '',
  response_template: '',
  auth_type: 'none',
  auth_token: '',
  parametersText: '',
  enabled: false,
});
const toolDraft = reactive(blankTool());

// The company's own AI meter. Read-only here: the quota and the key are set by
// the platform, this page only shows what the company has spent against them.
const usage = ref(null);
const usageLoading = ref(false);

const numberFormat = new Intl.NumberFormat('en');
const formatCredits = value => numberFormat.format(Number(value || 0));

const featureLabel = key => {
  const path = `TELETEL_ASSISTANT.USAGE.FEATURES.${String(key || '').toUpperCase()}`;
  const label = t(path);
  return label === path ? key : label;
};

const usageBarPct = computed(() => {
  const pct = usage.value?.entitlement?.usage_pct;
  return pct === null || pct === undefined ? 0 : Math.min(pct, 100);
});

// The tallest month sets the scale, so even a quiet company sees its shape.
const monthPeak = computed(() =>
  Math.max(1, ...(usage.value?.by_month || []).map(m => m.credits))
);

const monthBarHeight = month =>
  `${Math.round((month.credits / monthPeak.value) * 72) + 2}px`;

const loadUsage = async () => {
  usageLoading.value = true;
  try {
    const { data } = await AiUsageAPI.get();
    usage.value = data.payload;
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  } finally {
    usageLoading.value = false;
  }
};

// One parameter per line: name | type | description | required
const parseParameters = text =>
  String(text || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const [name, type, description, required] = line
        .split('|')
        .map(p => p.trim());
      return {
        name,
        type: type || 'string',
        description: description || '',
        // The placeholder tells the admin to write "required", so that very
        // word must count \u2014 as must the Arabic equivalents.
        required:
          /^(required|1|true|yes|\u0646\u0639\u0645|\u0645\u0637\u0644\u0648\u0628)$/i.test(
            required || ''
          ),
      };
    })
    .filter(p => p.name);

const formatParameters = list =>
  (list || [])
    .map(p =>
      [p.name, p.type, p.description, p.required ? 'required' : ''].join(' | ')
    )
    .join('\n');

// What the admin types to try a tool, per tool and per declared parameter.
const toolTestValues = reactive({});
const testValuesOf = tool => {
  if (!toolTestValues[tool.id]) toolTestValues[tool.id] = {};
  return toolTestValues[tool.id];
};

const loadTools = async () => {
  try {
    const { data } = await ToolsAPI.get();
    data.payload.forEach(testValuesOf);
    tools.value = data.payload;
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const editTool = tool => {
  Object.assign(toolDraft, blankTool(), {
    ...tool,
    auth_token: '',
    parametersText: formatParameters(tool.parameters),
  });
};

const resetTool = () => Object.assign(toolDraft, blankTool());

const saveTool = async () => {
  toolSaving.value = true;
  try {
    const payload = {
      title: toolDraft.title,
      description: toolDraft.description,
      endpoint_url: toolDraft.endpoint_url,
      http_method: toolDraft.http_method,
      request_template: toolDraft.request_template,
      response_template: toolDraft.response_template,
      auth_type: toolDraft.auth_type,
      enabled: toolDraft.enabled,
      param_schema: parseParameters(toolDraft.parametersText),
    };
    // The secret is only sent when the admin typed one, so editing a tool
    // never blanks a stored credential.
    if (toolDraft.auth_token) {
      payload.auth_config = { token: toolDraft.auth_token };
    }
    if (toolDraft.id) await ToolsAPI.update(toolDraft.id, payload);
    else await ToolsAPI.create(payload);
    resetTool();
    await loadTools();
    useAlert(t('TELETEL_ASSISTANT.TOOLS.SAVED'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  } finally {
    toolSaving.value = false;
  }
};

const toggleTool = async tool => {
  try {
    await ToolsAPI.update(tool.id, { enabled: !tool.enabled });
    await loadTools();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const deleteTool = async tool => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TELETEL_ASSISTANT.TOOLS.DELETE_CONFIRM'))) return;
  try {
    await ToolsAPI.delete(tool.id);
    await loadTools();
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const testTool = async tool => {
  toolTesting.value = tool.id;
  Object.assign(toolResult, { id: tool.id, ok: null, text: '' });
  try {
    const { data } = await ToolsAPI.test(tool.id, { ...testValuesOf(tool) });
    Object.assign(toolResult, {
      id: tool.id,
      ok: data.payload.ok,
      text: data.payload.ok ? data.payload.output : data.payload.error,
    });
  } catch (e) {
    Object.assign(toolResult, {
      id: tool.id,
      ok: false,
      text: e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'),
    });
  } finally {
    toolTesting.value = null;
  }
};

const blankDraft = () => ({
  name: '',
  description: '',
  product_name: '',
  handoff_message: '',
  guidelinesText: '',
  guardrailsText: '',
  // Autonomy switches (stored in the assistant's config, no migration).
  auto_resolve_hours: 0,
  resolution_message: '',
  learn_from_conversations: false,
  contact_memory: false,
  // Voice: how the assistant sounds. Empty = the behaviour before the
  // feature existed (mirror the customer, MSA, neutral, concise).
  language: '',
  reply_dialect: '',
  reply_tone: '',
  reply_length: '',
  reply_emoji: false,
  media_prompt: '',
});

// Dropdown keys; the labels live in the locale under VOICE.*.
const REPLY_LANGUAGES = ['', 'ar', 'en'];
const REPLY_DIALECTS = [
  '',
  'saudi',
  'gulf',
  'egyptian',
  'iraqi',
  'levantine',
  'maghrebi',
];
const REPLY_TONES = ['', 'formal', 'friendly', 'enthusiastic', 'empathetic'];
const REPLY_LENGTHS = ['', 'short', 'detailed'];
const draft = reactive(blankDraft());
const saving = ref(false);
// The form as it was last loaded or saved; a difference is unsaved work.
const draftSnapshot = ref(JSON.stringify(draft));
const draftDirty = computed(
  () => JSON.stringify(draft) !== draftSnapshot.value
);
const keepUnsavedDraft = () =>
  draftDirty.value &&
  // eslint-disable-next-line no-alert
  !window.confirm(t('TELETEL_ASSISTANT.BOTS.UNSAVED_CONFIRM'));

const linesOf = text =>
  String(text || '')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);

// `select` names the assistant to open once the list is fresh - after a save,
// the new record. Setting the id only after the list holds it means the
// selection watcher sees the real record, never a blank.
const loadBots = async ({ select = null } = {}) => {
  loading.value = true;
  try {
    const { data } = await AssistantAPI.listBots();
    bots.value = data.payload;
    aiAllowed.value = data.ai_allowed !== false;
    if (select) {
      selectedId.value = select;
    } else if (!creating.value && !selectedBot.value) {
      selectedId.value = preferredBotId(bots.value);
    }
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  } finally {
    loading.value = false;
  }
};

// Every per-assistant loader captures the id it was asked for and drops its
// answer if the selection moved meanwhile. Two selections in a row used to
// race, and whichever answer landed last painted the page - the saved
// assistant showed another one's knowledge and inboxes.
const stillSelected = id => id && selectedId.value === id;

const fillDraft = bot => {
  Object.assign(draft, blankDraft());
  draftSnapshot.value = JSON.stringify(draft);
  if (!bot) return;
  draft.name = bot.name || '';
  draft.description = bot.description || '';
  draft.product_name = bot.product_name || '';
  draft.handoff_message = bot.handoff_message || '';
  draft.guidelinesText = (bot.response_guidelines || []).join('\n');
  draft.guardrailsText = (bot.guardrails || []).join('\n');
  draft.auto_resolve_hours = Number(bot.auto_resolve_hours || 0);
  draft.resolution_message = bot.resolution_message || '';
  draft.learn_from_conversations = Boolean(bot.learn_from_conversations);
  draft.contact_memory = Boolean(bot.contact_memory);
  draft.language = bot.language || '';
  draft.reply_dialect = bot.reply_dialect || '';
  draft.reply_tone = bot.reply_tone || '';
  draft.reply_length = bot.reply_length || '';
  draft.reply_emoji = Boolean(bot.reply_emoji);
  draft.media_prompt = bot.media_prompt || '';
  draftSnapshot.value = JSON.stringify(draft);
};

let selectionBeforeNew = null;

const startNew = () => {
  if (keepUnsavedDraft()) return;
  selectionBeforeNew = selectedId.value;
  creating.value = true;
  selectedId.value = null;
  fillDraft(null);
  activeTab.value = 'bots';
};

const cancelNew = () => {
  creating.value = false;
  selectedId.value = selectionBeforeNew ?? preferredBotId(bots.value);
  selectionBeforeNew = null;
};

const selectBot = id => {
  if ((creating.value || id !== selectedId.value) && keepUnsavedDraft()) return;
  creating.value = false;
  selectionBeforeNew = null;
  selectedId.value = id;
};

const saveBot = async () => {
  saving.value = true;
  try {
    const payload = {
      name: draft.name,
      description: draft.description,
      product_name: draft.product_name,
      handoff_message: draft.handoff_message,
      response_guidelines: linesOf(draft.guidelinesText),
      guardrails: linesOf(draft.guardrailsText),
      auto_resolve_hours: Number(draft.auto_resolve_hours || 0),
      resolution_message: draft.resolution_message,
      learn_from_conversations: draft.learn_from_conversations,
      contact_memory: draft.contact_memory,
      language: draft.language,
      reply_dialect: draft.reply_dialect,
      reply_tone: draft.reply_tone,
      reply_length: draft.reply_length,
      reply_emoji: draft.reply_emoji,
      media_prompt: draft.media_prompt,
    };
    const { data } = selectedId.value
      ? await AssistantAPI.updateBot(selectedId.value, payload)
      : await AssistantAPI.createBot(payload);
    creating.value = false;
    selectionBeforeNew = null;
    await loadBots({ select: data.payload.id });
    // Saving the open assistant does not change the selection, so the form is
    // refreshed here; otherwise it would still count as unsaved.
    fillDraft(selectedBot.value);
    useAlert(t('TELETEL_ASSISTANT.BOTS.SAVED'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  } finally {
    saving.value = false;
  }
};

const deleteBot = async () => {
  if (!selectedId.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TELETEL_ASSISTANT.BOTS.DELETE_CONFIRM'))) return;
  try {
    await AssistantAPI.deleteBot(selectedId.value);
    selectedId.value = null;
    await loadBots();
    useAlert(t('TELETEL_ASSISTANT.BOTS.DELETED'));
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

// ---- FAQs
const faqs = ref([]);
const faqDraft = reactive({ id: null, question: '', answer: '' });
const faqSaving = ref(false);

// Bulk selection on the knowledge list: approve or delete many at once.
const selectedFaqIds = ref([]);
const toggleFaq = id => {
  selectedFaqIds.value = selectedFaqIds.value.includes(id)
    ? selectedFaqIds.value.filter(x => x !== id)
    : [...selectedFaqIds.value, id];
};
const toggleAllFaqs = () => {
  selectedFaqIds.value =
    selectedFaqIds.value.length === faqs.value.length
      ? []
      : faqs.value.map(f => f.id);
};
// Where a pending pair came from, for the reviewer.
const faqSourceLabel = faq => {
  const source = faq.source || {};
  if (source.kind === 'conversation')
    return t('TELETEL_ASSISTANT.FAQS.SOURCE_CONVERSATION', {
      id: source.display_id || source.id,
    });
  if (source.kind === 'document')
    return t('TELETEL_ASSISTANT.FAQS.SOURCE_DOCUMENT');
  return '';
};

const loadFaqs = async () => {
  const id = selectedId.value;
  if (!id) {
    faqs.value = [];
    return;
  }
  try {
    const { data } = await AssistantAPI.listFaqs(id);
    if (stillSelected(id)) faqs.value = data.payload;
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const bulkFaqs = async actionName => {
  if (!selectedFaqIds.value.length) return;
  // eslint-disable-next-line no-alert
  if (
    actionName === 'delete' &&
    !window.confirm(
      t('TELETEL_ASSISTANT.FAQS.BULK_DELETE_CONFIRM', {
        count: selectedFaqIds.value.length,
      })
    )
  )
    return;
  try {
    const { data } = await AssistantAPI.bulkFaqs(
      selectedId.value,
      selectedFaqIds.value,
      actionName
    );
    selectedFaqIds.value = [];
    await Promise.all([loadFaqs(), loadBots()]);
    useAlert(
      t('TELETEL_ASSISTANT.FAQS.BULK_DONE', { count: data.payload.count })
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const saveFaq = async () => {
  if (!selectedId.value) return;
  faqSaving.value = true;
  try {
    const payload = { question: faqDraft.question, answer: faqDraft.answer };
    if (faqDraft.id)
      await AssistantAPI.updateFaq(selectedId.value, faqDraft.id, payload);
    else await AssistantAPI.createFaq(selectedId.value, payload);
    Object.assign(faqDraft, { id: null, question: '', answer: '' });
    await Promise.all([loadFaqs(), loadBots()]);
    useAlert(t('TELETEL_ASSISTANT.FAQS.SAVED'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  } finally {
    faqSaving.value = false;
  }
};

const editFaq = faq => {
  Object.assign(faqDraft, {
    id: faq.id,
    question: faq.question,
    answer: faq.answer,
  });
};

const approveFaq = async faq => {
  try {
    await AssistantAPI.approveFaq(selectedId.value, faq.id);
    await Promise.all([loadFaqs(), loadBots()]);
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const deleteFaq = async faq => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TELETEL_ASSISTANT.FAQS.DELETE_CONFIRM'))) return;
  try {
    await AssistantAPI.deleteFaq(selectedId.value, faq.id);
    await Promise.all([loadFaqs(), loadBots()]);
    useAlert(t('TELETEL_ASSISTANT.FAQS.DELETED'));
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

// ---- Inboxes
const links = ref([]);
const inboxes = ref([]);

const loadLinks = async () => {
  const id = selectedId.value;
  if (!id) {
    links.value = [];
    inboxes.value = [];
    return;
  }
  try {
    const { data } = await AssistantAPI.listInboxLinks(id);
    if (!stillSelected(id)) return;
    links.value = data.payload;
    inboxes.value = data.inboxes;
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const linkInbox = async inbox => {
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(
      t('TELETEL_ASSISTANT.INBOXES.LINK_CONFIRM', { inbox: inbox.name })
    )
  )
    return;
  try {
    await AssistantAPI.linkInbox(selectedId.value, inbox.id);
    await Promise.all([loadLinks(), loadBots()]);
    useAlert(t('TELETEL_ASSISTANT.INBOXES.LINKED'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const unlinkInbox = async link => {
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(
      t('TELETEL_ASSISTANT.INBOXES.UNLINK_CONFIRM', { inbox: link.inbox_name })
    )
  )
    return;
  try {
    await AssistantAPI.unlinkInbox(selectedId.value, link.id);
    await Promise.all([loadLinks(), loadBots()]);
    useAlert(t('TELETEL_ASSISTANT.INBOXES.UNLINKED'));
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const unlinkedInboxes = computed(() =>
  inboxes.value.filter(i => !links.value.some(l => l.inbox_id === i.id))
);

// ---- Playground
const question = ref('');
const asking = ref(false);
const chat = ref([]);

const ask = async () => {
  if (!selectedId.value || !question.value.trim()) return;
  asking.value = true;
  const history = chat.value.map(m => ({ role: m.role, content: m.content }));
  const q = question.value.trim();
  question.value = '';
  chat.value.push({ role: 'user', content: q });
  try {
    const { data } = await AssistantAPI.ask(selectedId.value, q, history);
    chat.value.push({
      role: 'assistant',
      content: data.payload.handoff
        ? t('TELETEL_ASSISTANT.PLAYGROUND.HANDOFF')
        : data.payload.answer,
      handoff: data.payload.handoff,
      confidence: data.payload.confidence,
      faqs: data.payload.faqs,
      skippedTool: data.payload.skipped_tool,
    });
  } catch (e) {
    chat.value.push({
      role: 'assistant',
      content: e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'),
      error: true,
    });
  } finally {
    asking.value = false;
  }
};

// ---- Performance: what this assistant did lately, and what it could not answer.
const PERFORMANCE_TILES = [
  'conversations',
  'replies',
  'handoffs',
  'auto_resolved',
];
const performance = ref(null);
const performanceLoading = ref(false);

const loadPerformance = async () => {
  const id = selectedId.value;
  performance.value = null;
  if (!id) return;
  performanceLoading.value = true;
  try {
    const { data } = await AssistantAPI.performance(id);
    if (stillSelected(id)) performance.value = data.payload;
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  } finally {
    performanceLoading.value = false;
  }
};

const conversationPath = displayId =>
  `/app/accounts/${route.params.accountId}/conversations/${displayId}`;

// ---- Sources
const documents = ref([]);
const docDraft = reactive({
  name: '',
  url: '',
  text: '',
  crawl: false,
  auto_sync: false,
});

// Bulk selection on the sources list: delete or re-read many at once.
const selectedDocIds = ref([]);
const toggleDoc = id => {
  selectedDocIds.value = selectedDocIds.value.includes(id)
    ? selectedDocIds.value.filter(x => x !== id)
    : [...selectedDocIds.value, id];
};
const toggleAllDocs = () => {
  selectedDocIds.value =
    selectedDocIds.value.length === documents.value.length
      ? []
      : documents.value.map(d => d.id);
};
// ---- Scenarios: one named way of handling one kind of request.
const scenarios = ref([]);
const scenarioTools = ref([]);
// The account's teams, for «which team receives the hand-off».
const scenarioTeams = ref([]);
const scenarioSaving = ref(false);
const blankScenario = () => ({
  id: null,
  title: '',
  description: '',
  instruction: '',
  tools: [],
  enabled: true,
  // The specialist face of a scenario: a persona the assistant introduces
  // itself with, the team that takes the hand-off, its own hand-off message.
  persona: '',
  handoff_team_id: null,
  handoff_message: '',
});
const teamName = id => scenarioTeams.value.find(x => x.id === id)?.name || '';
const scenarioDraft = reactive(blankScenario());

const loadScenarios = async () => {
  const id = selectedId.value;
  if (!id) {
    scenarios.value = [];
    return;
  }
  try {
    const { data } = await AssistantAPI.listScenarios(id);
    if (!stillSelected(id)) return;
    scenarios.value = data.payload;
    scenarioTools.value = data.tools || [];
    scenarioTeams.value = data.teams || [];
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const editScenario = scenario =>
  Object.assign(scenarioDraft, blankScenario(), {
    ...scenario,
    tools: [...(scenario.tools || [])],
    persona: scenario.persona || '',
    handoff_team_id: scenario.handoff_team_id || null,
    handoff_message: scenario.handoff_message || '',
  });
const resetScenario = () => Object.assign(scenarioDraft, blankScenario());

const toggleScenarioTool = slug => {
  scenarioDraft.tools = scenarioDraft.tools.includes(slug)
    ? scenarioDraft.tools.filter(s => s !== slug)
    : [...scenarioDraft.tools, slug];
};

const saveScenario = async () => {
  if (!selectedId.value) return;
  scenarioSaving.value = true;
  try {
    const payload = {
      title: scenarioDraft.title,
      description: scenarioDraft.description,
      instruction: scenarioDraft.instruction,
      tools: scenarioDraft.tools,
      enabled: scenarioDraft.enabled,
      persona: scenarioDraft.persona,
      // An empty string clears the team; the server keeps only its own teams.
      handoff_team_id: scenarioDraft.handoff_team_id || '',
      handoff_message: scenarioDraft.handoff_message,
    };
    if (scenarioDraft.id)
      await AssistantAPI.updateScenario(
        selectedId.value,
        scenarioDraft.id,
        payload
      );
    else await AssistantAPI.createScenario(selectedId.value, payload);
    resetScenario();
    await Promise.all([loadScenarios(), loadBots()]);
    useAlert(t('TELETEL_ASSISTANT.SCENARIOS.SAVED'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  } finally {
    scenarioSaving.value = false;
  }
};

const toggleScenarioEnabled = async scenario => {
  try {
    await AssistantAPI.updateScenario(selectedId.value, scenario.id, {
      enabled: !scenario.enabled,
    });
    await loadScenarios();
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const deleteScenario = async scenario => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TELETEL_ASSISTANT.SCENARIOS.DELETE_CONFIRM'))) return;
  try {
    await AssistantAPI.deleteScenario(selectedId.value, scenario.id);
    await Promise.all([loadScenarios(), loadBots()]);
    useAlert(t('TELETEL_ASSISTANT.SCENARIOS.DELETED'));
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};
const docSaving = ref(false);
let docTimer = null;
const loadDocuments = async () => {
  const id = selectedId.value;
  if (!id) {
    documents.value = [];
    return;
  }
  try {
    const { data } = await AssistantAPI.listDocuments(id);
    if (stillSelected(id)) documents.value = data.payload;
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const bulkDocuments = async actionName => {
  if (!selectedDocIds.value.length) return;
  // eslint-disable-next-line no-alert
  if (
    actionName === 'delete' &&
    !window.confirm(
      t('TELETEL_ASSISTANT.DOCUMENTS.BULK_DELETE_CONFIRM', {
        count: selectedDocIds.value.length,
      })
    )
  )
    return;
  try {
    const { data } = await AssistantAPI.bulkDocuments(
      selectedId.value,
      selectedDocIds.value,
      actionName
    );
    selectedDocIds.value = [];
    await Promise.all([loadDocuments(), loadBots()]);
    useAlert(
      t('TELETEL_ASSISTANT.DOCUMENTS.BULK_DONE', { count: data.payload.count })
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const addDocument = async () => {
  if (!selectedId.value || (!docDraft.url.trim() && !docDraft.text.trim()))
    return;
  docSaving.value = true;
  try {
    await AssistantAPI.addDocument(selectedId.value, {
      name: docDraft.name,
      url: docDraft.url.trim() || undefined,
      text: docDraft.text.trim() || undefined,
      crawl: docDraft.crawl,
      auto_sync: docDraft.auto_sync,
    });
    Object.assign(docDraft, {
      name: '',
      url: '',
      text: '',
      crawl: false,
      auto_sync: false,
    });
    await loadDocuments();
    useAlert(t('TELETEL_ASSISTANT.DOCUMENTS.ADDED'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT.API_ERROR'));
  } finally {
    docSaving.value = false;
  }
};

const reingestDocument = async doc => {
  try {
    await AssistantAPI.reingestDocument(selectedId.value, doc.id);
    await loadDocuments();
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const deleteDocument = async doc => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TELETEL_ASSISTANT.DOCUMENTS.DELETE_CONFIRM'))) return;
  try {
    await AssistantAPI.deleteDocument(selectedId.value, doc.id);
    await Promise.all([loadDocuments(), loadFaqs(), loadBots()]);
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const pendingCount = computed(
  () => faqs.value.filter(f => f.status !== 'approved').length
);

const approveAll = async (documentId = null) => {
  const count = documentId
    ? documents.value.find(d => d.id === documentId)?.pending_faqs || 0
    : pendingCount.value;
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(t('TELETEL_ASSISTANT.FAQS.APPROVE_ALL_CONFIRM', { count }))
  )
    return;
  try {
    const { data } = await AssistantAPI.approveAllFaqs(
      selectedId.value,
      documentId
    );
    await Promise.all([loadFaqs(), loadDocuments(), loadBots()]);
    useAlert(
      t('TELETEL_ASSISTANT.FAQS.APPROVED_ALL', { count: data.payload.approved })
    );
  } catch (e) {
    useAlert(t('TELETEL_ASSISTANT.API_ERROR'));
  }
};

const hasIngesting = computed(() =>
  documents.value.some(d => d.sync_status === 'syncing')
);

// Poll while a source is being processed so the list updates by itself.
watch(hasIngesting, busy => {
  if (docTimer) clearInterval(docTimer);
  docTimer = null;
  if (busy) {
    docTimer = setInterval(() => {
      loadDocuments();
      loadFaqs();
    }, 5000);
  }
});

watch(activeTab, tab => {
  if (tab === 'tools' && !tools.value.length) loadTools();
  if (tab === 'usage') loadUsage();
  if (tab === 'performance') loadPerformance();
});

watch(selectedId, id => {
  if (!creating.value) fillDraft(selectedBot.value);
  rememberBot(id);
  chat.value = [];
  selectedFaqIds.value = [];
  selectedDocIds.value = [];
  resetScenario();
  loadFaqs();
  loadLinks();
  loadDocuments();
  loadScenarios();
  if (activeTab.value === 'performance') loadPerformance();
});

// Opened by its address, the page mounts before the account payload arrives,
// and the grant reads as off for that moment. Loading only at mount left the
// list empty for good - «لا يوجد مساعد بعد» on an account that has some. So the
// load follows the grant, whenever it turns on.
watch(
  available,
  async on => {
    if (!on) return;
    // loadBots picks the assistant; the selection watcher loads the rest.
    await loadBots();
    if (!selectedId.value) fillDraft(null);
    if (activeTab.value === 'tools') loadTools();
    if (activeTab.value === 'usage') loadUsage();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader
      :title="t('TELETEL_ASSISTANT.HEADER')"
      :description="t('TELETEL_ASSISTANT.DESCRIPTION')"
    />

    <ContentCard v-if="!available">
      <p class="text-sm text-n-slate-11">
        {{ t('TELETEL_ASSISTANT.DISABLED') }}
      </p>
    </ContentCard>

    <template v-else>
      <p
        v-if="!aiAllowed"
        class="rounded-lg border border-n-amber-7 bg-n-amber-2 px-3 py-2 text-sm text-n-amber-12"
      >
        {{ t('TELETEL_ASSISTANT.AI_DENIED') }}
      </p>

      <!-- Every assistant, always visible. A dropdown hid the list, and two
           assistants with the same name were indistinguishable in it; the
           cards carry the counts and the last change, and the open one is
           marked. -->
      <ContentCard :title="t('TELETEL_ASSISTANT.BOTS.LIST_TITLE')">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p class="text-xs text-n-slate-10">
            {{ t('TELETEL_ASSISTANT.BOTS.LIST_HINT') }}
          </p>
          <Button
            size="sm"
            icon="i-lucide-plus"
            :label="t('TELETEL_ASSISTANT.BOTS.NEW')"
            @click="startNew"
          />
        </div>
        <p v-if="!bots.length" class="text-sm text-n-slate-11">
          {{ t('TELETEL_ASSISTANT.BOTS.EMPTY') }}
        </p>
        <div v-else class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <button
            v-for="b in sortedBots"
            :key="b.id"
            type="button"
            class="group relative flex flex-col gap-3 rounded-xl border p-4 text-start transition-all"
            :class="
              b.id === selectedId && !creating
                ? 'border-n-brand bg-n-brand/5 shadow-md ring-2 ring-n-brand/40'
                : 'border-n-weak bg-teletel-panel hover:-translate-y-0.5 hover:border-n-slate-8 hover:shadow-md'
            "
            @click="selectBot(b.id)"
          >
            <span class="flex items-start gap-3">
              <span
                class="grid size-11 shrink-0 place-content-center rounded-full"
                :class="
                  b.id === selectedId && !creating
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-blue-11'
                "
              >
                <span class="i-lucide-bot size-6" />
              </span>
              <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                <span class="flex items-center gap-2">
                  <span
                    class="truncate text-base font-semibold text-n-slate-12"
                  >
                    {{ b.name }}
                  </span>
                  <span
                    v-if="b.id === selectedId && !creating"
                    class="shrink-0 rounded-full bg-n-brand px-2 py-0.5 text-[11px] font-medium text-white"
                  >
                    {{ t('TELETEL_ASSISTANT.BOTS.OPEN') }}
                  </span>
                </span>
                <span class="line-clamp-2 text-xs text-n-slate-11">
                  {{ b.description }}
                </span>
              </span>
            </span>

            <!-- One chip says whether this assistant would answer a customer
                 today: knowledge + a linked inbox. The owner had the knowledge
                 on one assistant and the inboxes on another. -->
            <span
              class="inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
              :class="readinessClass(b)"
            >
              <span :class="readinessIcon(b)" class="size-3.5" />
              {{ t(`TELETEL_ASSISTANT.BOTS.READINESS.${readiness(b)}`) }}
            </span>

            <span class="flex flex-wrap gap-1.5 text-xs">
              <span
                class="rounded-md px-2 py-0.5"
                :class="
                  b.faqs_count
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ t('TELETEL_ASSISTANT.BOTS.CHIP_FAQS', { n: b.faqs_count }) }}
              </span>
              <span
                v-if="b.pending_faqs_count"
                class="rounded-md bg-n-amber-3 px-2 py-0.5 text-n-amber-11"
              >
                {{
                  t('TELETEL_ASSISTANT.BOTS.CHIP_PENDING', {
                    n: b.pending_faqs_count,
                  })
                }}
              </span>
              <span
                class="rounded-md px-2 py-0.5"
                :class="
                  b.inbox_ids.length
                    ? 'bg-n-blue-3 text-n-blue-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{
                  t('TELETEL_ASSISTANT.BOTS.CHIP_INBOXES', {
                    n: b.inbox_ids.length,
                  })
                }}
              </span>
              <span class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-n-slate-11">
                {{
                  t('TELETEL_ASSISTANT.BOTS.CHIP_SOURCES', {
                    n: b.documents_count,
                  })
                }}
              </span>
            </span>

            <span
              class="flex items-center justify-between text-[11px] text-n-slate-9"
            >
              <span>#{{ b.id }}</span>
              <span>{{ formatUpdated(b.updated_at) }}</span>
            </span>
          </button>
        </div>
      </ContentCard>

      <p
        v-if="creating"
        class="rounded-lg border border-n-blue-7 bg-n-blue-2 px-3 py-2 text-sm text-n-blue-12"
      >
        {{ t('TELETEL_ASSISTANT.BOTS.CREATING_HINT') }}
      </p>
      <p
        v-else-if="!selectedId && bots.length"
        class="rounded-lg border border-n-amber-7 bg-n-amber-2 px-3 py-2 text-sm text-n-amber-12"
      >
        {{ t('TELETEL_ASSISTANT.BOTS.NO_SELECTION') }}
      </p>

      <div class="flex flex-wrap gap-1 border-b border-n-weak">
        <button
          v-for="tab in TABS"
          :key="tab"
          type="button"
          class="px-3 py-2 text-sm"
          :class="
            activeTab === tab
              ? 'border-b-2 border-n-brand text-n-slate-12'
              : 'text-n-slate-11'
          "
          @click="activeTab = tab"
        >
          {{ t(`TELETEL_ASSISTANT.TABS.${tab.toUpperCase()}`) }}
        </button>
      </div>

      <!-- Assistants -->
      <ContentCard
        v-if="activeTab === 'bots'"
        :title="
          selectedBot && !creating
            ? t('TELETEL_ASSISTANT.BOTS.EDITING', { name: selectedBot.name })
            : t('TELETEL_ASSISTANT.BOTS.CREATING')
        "
      >
        <form class="grid gap-3 md:grid-cols-2" @submit.prevent="saveBot">
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('TELETEL_ASSISTANT.BOTS.NAME') }}
            <input
              v-model="draft.name"
              type="text"
              required
              class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('TELETEL_ASSISTANT.BOTS.PRODUCT_NAME') }}
            <input
              v-model="draft.product_name"
              type="text"
              class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-sm text-n-slate-12 md:col-span-2"
          >
            {{ t('TELETEL_ASSISTANT.BOTS.DESCRIPTION') }}
            <input
              v-model="draft.description"
              type="text"
              required
              class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-sm text-n-slate-12 md:col-span-2"
          >
            {{ t('TELETEL_ASSISTANT.BOTS.HANDOFF_MESSAGE') }}
            <input
              v-model="draft.handoff_message"
              type="text"
              class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-sm text-n-slate-12 md:col-span-2"
          >
            {{ t('TELETEL_ASSISTANT.BOTS.MEDIA_PROMPT') }}
            <input
              v-model="draft.media_prompt"
              type="text"
              maxlength="500"
              :placeholder="
                t('TELETEL_ASSISTANT.BOTS.MEDIA_PROMPT_PLACEHOLDER')
              "
              class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            />
            <span class="text-xs text-n-slate-10">
              {{ t('TELETEL_ASSISTANT.BOTS.MEDIA_PROMPT_HINT') }}
            </span>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('TELETEL_ASSISTANT.BOTS.GUIDELINES') }}
            <textarea
              v-model="draft.guidelinesText"
              rows="5"
              class="mb-0 rounded-lg border border-n-weak bg-teletel-panel px-2 py-1 text-sm"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('TELETEL_ASSISTANT.BOTS.GUARDRAILS') }}
            <textarea
              v-model="draft.guardrailsText"
              rows="5"
              class="mb-0 rounded-lg border border-n-weak bg-teletel-panel px-2 py-1 text-sm"
            />
          </label>

          <!-- Voice: how the assistant sounds — language, dialect, tone,
               length, emoji. Defaults reproduce the old behaviour exactly. -->
          <div
            class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3 md:col-span-2"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('TELETEL_ASSISTANT.BOTS.VOICE.TITLE') }}
            </span>
            <p class="text-xs text-n-slate-10">
              {{ t('TELETEL_ASSISTANT.BOTS.VOICE.HINT') }}
            </p>
            <div class="grid gap-3 md:grid-cols-2">
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ t('TELETEL_ASSISTANT.BOTS.VOICE.LANGUAGE') }}
                <select
                  v-model="draft.language"
                  class="!mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
                >
                  <option
                    v-for="opt in REPLY_LANGUAGES"
                    :key="`lang-${opt}`"
                    :value="opt"
                  >
                    {{
                      t(
                        `TELETEL_ASSISTANT.BOTS.VOICE.LANGUAGES.${(opt || 'default').toUpperCase()}`
                      )
                    }}
                  </option>
                </select>
              </label>
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ t('TELETEL_ASSISTANT.BOTS.VOICE.DIALECT') }}
                <select
                  v-model="draft.reply_dialect"
                  :disabled="draft.language === 'en'"
                  class="!mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm disabled:opacity-50"
                >
                  <option
                    v-for="opt in REPLY_DIALECTS"
                    :key="`dialect-${opt}`"
                    :value="opt"
                  >
                    {{
                      t(
                        `TELETEL_ASSISTANT.BOTS.VOICE.DIALECTS.${(opt || 'default').toUpperCase()}`
                      )
                    }}
                  </option>
                </select>
                <span
                  v-if="draft.language === 'en'"
                  class="text-xs text-n-slate-10"
                >
                  {{ t('TELETEL_ASSISTANT.BOTS.VOICE.DIALECT_ENGLISH_HINT') }}
                </span>
              </label>
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ t('TELETEL_ASSISTANT.BOTS.VOICE.TONE') }}
                <select
                  v-model="draft.reply_tone"
                  class="!mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
                >
                  <option
                    v-for="opt in REPLY_TONES"
                    :key="`tone-${opt}`"
                    :value="opt"
                  >
                    {{
                      t(
                        `TELETEL_ASSISTANT.BOTS.VOICE.TONES.${(opt || 'default').toUpperCase()}`
                      )
                    }}
                  </option>
                </select>
              </label>
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ t('TELETEL_ASSISTANT.BOTS.VOICE.LENGTH') }}
                <select
                  v-model="draft.reply_length"
                  class="!mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
                >
                  <option
                    v-for="opt in REPLY_LENGTHS"
                    :key="`length-${opt}`"
                    :value="opt"
                  >
                    {{
                      t(
                        `TELETEL_ASSISTANT.BOTS.VOICE.LENGTHS.${(opt || 'default').toUpperCase()}`
                      )
                    }}
                  </option>
                </select>
              </label>
              <label class="flex items-start gap-2 text-sm text-n-slate-12">
                <input
                  v-model="draft.reply_emoji"
                  type="checkbox"
                  class="mt-1"
                />
                <span class="flex flex-col">
                  {{ t('TELETEL_ASSISTANT.BOTS.VOICE.EMOJI') }}
                  <span class="text-xs text-n-slate-10">
                    {{ t('TELETEL_ASSISTANT.BOTS.VOICE.EMOJI_HINT') }}
                  </span>
                </span>
              </label>
            </div>
          </div>

          <!-- Autonomy: what the assistant does on its own, after the reply. -->
          <div
            class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3 md:col-span-2"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.TITLE') }}
            </span>
            <p class="text-xs text-n-slate-10">
              {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.HINT') }}
            </p>
            <div class="grid gap-3 md:grid-cols-2">
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.AUTO_RESOLVE_HOURS') }}
                <select
                  v-model.number="draft.auto_resolve_hours"
                  class="!mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
                >
                  <option :value="0">
                    {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.OFF') }}
                  </option>
                  <option
                    v-for="h in [1, 2, 4, 8, 12, 24, 48, 72, 168]"
                    :key="h"
                    :value="h"
                  >
                    {{
                      t('TELETEL_ASSISTANT.BOTS.AUTONOMY.AFTER_HOURS', {
                        hours: h,
                      })
                    }}
                  </option>
                </select>
                <span class="text-xs text-n-slate-10">
                  {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.AUTO_RESOLVE_HINT') }}
                </span>
              </label>
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.RESOLUTION_MESSAGE') }}
                <input
                  v-model="draft.resolution_message"
                  type="text"
                  :placeholder="
                    t('TELETEL_ASSISTANT.BOTS.AUTONOMY.RESOLUTION_PLACEHOLDER')
                  "
                  class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
                />
              </label>
              <label class="flex items-start gap-2 text-sm text-n-slate-12">
                <input
                  v-model="draft.learn_from_conversations"
                  type="checkbox"
                  class="mt-1"
                />
                <span class="flex flex-col">
                  {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.LEARN') }}
                  <span class="text-xs text-n-slate-10">
                    {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.LEARN_HINT') }}
                  </span>
                </span>
              </label>
              <label class="flex items-start gap-2 text-sm text-n-slate-12">
                <input
                  v-model="draft.contact_memory"
                  type="checkbox"
                  class="mt-1"
                />
                <span class="flex flex-col">
                  {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.MEMORY') }}
                  <span class="text-xs text-n-slate-10">
                    {{ t('TELETEL_ASSISTANT.BOTS.AUTONOMY.MEMORY_HINT') }}
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div class="flex gap-2 md:col-span-2">
            <Button
              type="submit"
              size="sm"
              :label="t('TELETEL_ASSISTANT.BOTS.SAVE')"
              :is-loading="saving"
            />
            <Button
              v-if="creating && bots.length"
              type="button"
              size="sm"
              variant="outline"
              :label="t('TELETEL_ASSISTANT.BOTS.CANCEL')"
              @click="cancelNew"
            />
            <Button
              v-if="selectedId && !creating"
              type="button"
              size="sm"
              variant="outline"
              color="ruby"
              :label="t('TELETEL_ASSISTANT.BOTS.DELETE')"
              @click="deleteBot"
            />
          </div>
        </form>
      </ContentCard>

      <!-- Knowledge, inboxes, playground and sources belong to one assistant;
           say so instead of showing nothing while none is open. -->
      <ContentCard
        v-if="
          !selectedId &&
          [
            'faqs',
            'inboxes',
            'playground',
            'documents',
            'scenarios',
            'performance',
          ].includes(activeTab)
        "
        :title="t(`TELETEL_ASSISTANT.TABS.${activeTab.toUpperCase()}`)"
      >
        <p class="text-sm text-n-slate-11">
          {{ t('TELETEL_ASSISTANT.BOTS.SELECT_FIRST') }}
        </p>
      </ContentCard>

      <!-- FAQs -->
      <ContentCard
        v-if="activeTab === 'faqs' && selectedId"
        :title="t('TELETEL_ASSISTANT.FAQS.TITLE')"
      >
        <p class="mb-3 text-xs text-n-slate-10">
          {{ t('TELETEL_ASSISTANT.FAQS.HINT') }}
        </p>
        <div v-if="pendingCount" class="mb-3">
          <Button
            size="xs"
            variant="outline"
            :label="
              t('TELETEL_ASSISTANT.FAQS.APPROVE_ALL', { count: pendingCount })
            "
            @click="approveAll()"
          />
        </div>
        <form class="mb-4 grid gap-2" @submit.prevent="saveFaq">
          <input
            v-model="faqDraft.question"
            type="text"
            required
            :placeholder="t('TELETEL_ASSISTANT.FAQS.QUESTION')"
            class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
          />
          <textarea
            v-model="faqDraft.answer"
            rows="3"
            required
            :placeholder="t('TELETEL_ASSISTANT.FAQS.ANSWER')"
            class="mb-0 rounded-lg border border-n-weak bg-teletel-panel px-2 py-1 text-sm"
          />
          <div>
            <Button
              type="submit"
              size="sm"
              :label="
                faqDraft.id
                  ? t('TELETEL_ASSISTANT.FAQS.EDIT')
                  : t('TELETEL_ASSISTANT.FAQS.ADD')
              "
              :is-loading="faqSaving"
            />
          </div>
        </form>
        <p v-if="!faqs.length" class="text-sm text-n-slate-11">
          {{ t('TELETEL_ASSISTANT.FAQS.EMPTY') }}
        </p>
        <!-- Bulk bar: select many, act once. -->
        <div
          v-if="faqs.length"
          class="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-xs"
        >
          <label class="flex items-center gap-2 text-n-slate-12">
            <input
              type="checkbox"
              :checked="selectedFaqIds.length === faqs.length"
              @change="toggleAllFaqs"
            />
            {{ t('TELETEL_ASSISTANT.FAQS.SELECT_ALL') }}
          </label>
          <span class="text-n-slate-10">
            {{
              t('TELETEL_ASSISTANT.FAQS.SELECTED', {
                count: selectedFaqIds.length,
              })
            }}
          </span>
          <Button
            size="xs"
            variant="outline"
            :disabled="!selectedFaqIds.length"
            :label="t('TELETEL_ASSISTANT.FAQS.BULK_APPROVE')"
            @click="bulkFaqs('approve')"
          />
          <Button
            size="xs"
            variant="outline"
            color="ruby"
            :disabled="!selectedFaqIds.length"
            :label="t('TELETEL_ASSISTANT.FAQS.BULK_DELETE')"
            @click="bulkFaqs('delete')"
          />
        </div>
        <ul class="divide-y divide-n-weak">
          <li
            v-for="faq in faqs"
            :key="faq.id"
            class="flex flex-col gap-1 py-3"
          >
            <div class="flex flex-wrap items-center gap-2">
              <input
                type="checkbox"
                :checked="selectedFaqIds.includes(faq.id)"
                @change="toggleFaq(faq.id)"
              />
              <span class="font-medium text-n-slate-12">{{
                faq.question
              }}</span>
              <span
                v-if="faqSourceLabel(faq)"
                class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
              >
                {{ faqSourceLabel(faq) }}
              </span>
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="
                  faq.status === 'approved'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-amber-3 text-n-amber-11'
                "
              >
                {{
                  faq.status === 'approved'
                    ? t('TELETEL_ASSISTANT.FAQS.APPROVED')
                    : t('TELETEL_ASSISTANT.FAQS.PENDING')
                }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{
                  faq.embedded
                    ? t('TELETEL_ASSISTANT.FAQS.EMBEDDED')
                    : t('TELETEL_ASSISTANT.FAQS.NOT_EMBEDDED')
                }}
              </span>
            </div>
            <p class="whitespace-pre-line text-sm text-n-slate-11">
              {{ faq.answer }}
            </p>
            <div class="flex gap-2">
              <Button
                v-if="faq.status !== 'approved'"
                size="xs"
                variant="outline"
                :label="t('TELETEL_ASSISTANT.FAQS.APPROVE')"
                @click="approveFaq(faq)"
              />
              <Button
                size="xs"
                variant="ghost"
                :label="t('TELETEL_ASSISTANT.FAQS.EDIT')"
                @click="editFaq(faq)"
              />
              <Button
                size="xs"
                variant="ghost"
                color="ruby"
                :label="t('TELETEL_ASSISTANT.FAQS.DELETE')"
                @click="deleteFaq(faq)"
              />
            </div>
          </li>
        </ul>
      </ContentCard>

      <!-- Scenarios: one named way of handling one kind of request -->
      <ContentCard
        v-if="activeTab === 'scenarios' && selectedId"
        :title="t('TELETEL_ASSISTANT.SCENARIOS.TITLE')"
      >
        <p class="mb-3 text-xs text-n-slate-10">
          {{ t('TELETEL_ASSISTANT.SCENARIOS.HINT') }}
        </p>
        <form class="mb-4 grid gap-2" @submit.prevent="saveScenario">
          <input
            v-model="scenarioDraft.title"
            type="text"
            required
            :placeholder="t('TELETEL_ASSISTANT.SCENARIOS.NAME')"
            class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
          />
          <input
            v-model="scenarioDraft.description"
            type="text"
            required
            :placeholder="t('TELETEL_ASSISTANT.SCENARIOS.DESCRIPTION')"
            class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
          />
          <textarea
            v-model="scenarioDraft.instruction"
            rows="4"
            required
            :placeholder="t('TELETEL_ASSISTANT.SCENARIOS.INSTRUCTION')"
            class="mb-0 rounded-lg border border-n-weak bg-teletel-panel px-2 py-1 text-sm"
          />
          <!-- The specialist face: persona, hand-off team, hand-off message. -->
          <div
            class="grid gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3 md:grid-cols-2"
          >
            <p class="text-xs text-n-slate-10 md:col-span-2">
              {{ t('TELETEL_ASSISTANT.SCENARIOS.SPECIALIST_HINT') }}
            </p>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('TELETEL_ASSISTANT.SCENARIOS.PERSONA') }}
              <input
                v-model="scenarioDraft.persona"
                type="text"
                maxlength="80"
                :placeholder="
                  t('TELETEL_ASSISTANT.SCENARIOS.PERSONA_PLACEHOLDER')
                "
                class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('TELETEL_ASSISTANT.SCENARIOS.HANDOFF_TEAM') }}
              <select
                v-model="scenarioDraft.handoff_team_id"
                class="!mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
              >
                <option :value="null">
                  {{ t('TELETEL_ASSISTANT.SCENARIOS.HANDOFF_TEAM_NONE') }}
                </option>
                <option
                  v-for="team in scenarioTeams"
                  :key="team.id"
                  :value="team.id"
                >
                  {{ team.name }}
                </option>
              </select>
            </label>
            <label
              class="flex flex-col gap-1 text-sm text-n-slate-12 md:col-span-2"
            >
              {{ t('TELETEL_ASSISTANT.SCENARIOS.HANDOFF_MESSAGE') }}
              <input
                v-model="scenarioDraft.handoff_message"
                type="text"
                maxlength="500"
                :placeholder="
                  t('TELETEL_ASSISTANT.SCENARIOS.HANDOFF_MESSAGE_PLACEHOLDER')
                "
                class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
              />
            </label>
          </div>
          <div v-if="scenarioTools.length" class="flex flex-col gap-1 text-sm">
            <span class="text-xs text-n-slate-10">
              {{ t('TELETEL_ASSISTANT.SCENARIOS.TOOLS') }}
            </span>
            <div class="flex flex-wrap gap-2">
              <label
                v-for="tool in scenarioTools"
                :key="tool.slug"
                class="flex items-center gap-1 rounded-lg border border-n-weak px-2 py-1 text-xs text-n-slate-12"
              >
                <input
                  type="checkbox"
                  :checked="scenarioDraft.tools.includes(tool.slug)"
                  @change="toggleScenarioTool(tool.slug)"
                />
                {{ tool.title }}
              </label>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('TELETEL_ASSISTANT.SCENARIOS.NO_TOOLS') }}
          </p>
          <div class="flex gap-2">
            <Button
              type="submit"
              size="sm"
              :label="
                scenarioDraft.id
                  ? t('TELETEL_ASSISTANT.SCENARIOS.EDIT')
                  : t('TELETEL_ASSISTANT.SCENARIOS.ADD')
              "
              :is-loading="scenarioSaving"
            />
            <Button
              v-if="scenarioDraft.id"
              type="button"
              size="sm"
              variant="outline"
              :label="t('TELETEL_ASSISTANT.SCENARIOS.CANCEL')"
              @click="resetScenario"
            />
          </div>
        </form>
        <p v-if="!scenarios.length" class="text-sm text-n-slate-11">
          {{ t('TELETEL_ASSISTANT.SCENARIOS.EMPTY') }}
        </p>
        <ul class="divide-y divide-n-weak">
          <li
            v-for="scenario in scenarios"
            :key="scenario.id"
            class="flex flex-col gap-1 py-3"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-medium text-n-slate-12">
                {{ scenario.title }}
              </span>
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="
                  scenario.enabled
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{
                  scenario.enabled
                    ? t('TELETEL_ASSISTANT.SCENARIOS.ENABLED')
                    : t('TELETEL_ASSISTANT.SCENARIOS.DISABLED')
                }}
              </span>
              <span
                v-if="scenario.tools.length"
                class="text-xs text-n-slate-10"
              >
                {{
                  t('TELETEL_ASSISTANT.SCENARIOS.USES_TOOLS', {
                    count: scenario.tools.length,
                  })
                }}
              </span>
              <span
                v-if="scenario.persona"
                class="rounded-full bg-n-blue-3 px-2 py-0.5 text-xs text-n-blue-11"
              >
                {{ scenario.persona }}
              </span>
              <span
                v-if="scenario.handoff_team_id"
                class="rounded-full bg-n-amber-3 px-2 py-0.5 text-xs text-n-amber-11"
              >
                {{
                  t('TELETEL_ASSISTANT.SCENARIOS.HANDS_OFF_TO', {
                    team: teamName(scenario.handoff_team_id),
                  })
                }}
              </span>
            </div>
            <p class="text-sm text-n-slate-11">{{ scenario.description }}</p>
            <p class="whitespace-pre-line text-xs text-n-slate-10">
              {{ scenario.instruction }}
            </p>
            <div class="flex gap-2">
              <Button
                size="xs"
                variant="ghost"
                :label="t('TELETEL_ASSISTANT.SCENARIOS.EDIT')"
                @click="editScenario(scenario)"
              />
              <Button
                size="xs"
                variant="ghost"
                :label="
                  scenario.enabled
                    ? t('TELETEL_ASSISTANT.SCENARIOS.DISABLE')
                    : t('TELETEL_ASSISTANT.SCENARIOS.ENABLE')
                "
                @click="toggleScenarioEnabled(scenario)"
              />
              <Button
                size="xs"
                variant="ghost"
                color="ruby"
                :label="t('TELETEL_ASSISTANT.SCENARIOS.DELETE')"
                @click="deleteScenario(scenario)"
              />
            </div>
          </li>
        </ul>
      </ContentCard>

      <!-- Inboxes -->
      <ContentCard
        v-if="activeTab === 'inboxes' && selectedId"
        :title="t('TELETEL_ASSISTANT.INBOXES.TITLE')"
      >
        <p class="mb-3 text-xs text-n-slate-10">
          {{ t('TELETEL_ASSISTANT.INBOXES.HINT') }}
        </p>
        <p v-if="!links.length" class="mb-3 text-sm text-n-slate-11">
          {{ t('TELETEL_ASSISTANT.INBOXES.EMPTY') }}
        </p>
        <ul class="mb-4 divide-y divide-n-weak">
          <li
            v-for="link in links"
            :key="link.id"
            class="flex items-center justify-between py-2"
          >
            <span class="text-sm text-n-slate-12"
              >{{ link.inbox_name }}
              <span class="text-xs text-n-slate-10"
                >({{ link.channel_type }})</span
              ></span
            >
            <Button
              size="xs"
              variant="outline"
              color="ruby"
              :label="t('TELETEL_ASSISTANT.INBOXES.UNLINK')"
              @click="unlinkInbox(link)"
            />
          </li>
        </ul>
        <ul class="divide-y divide-n-weak">
          <li
            v-for="inbox in unlinkedInboxes"
            :key="inbox.id"
            class="flex items-center justify-between py-2"
          >
            <span class="text-sm text-n-slate-12">
              {{ inbox.name }}
              <span v-if="inbox.linked_bot_id" class="text-xs text-n-amber-11">
                — {{ t('TELETEL_ASSISTANT.INBOXES.TAKEN') }}
              </span>
            </span>
            <Button
              size="xs"
              variant="outline"
              :disabled="Boolean(inbox.linked_bot_id)"
              :label="t('TELETEL_ASSISTANT.INBOXES.LINK')"
              @click="linkInbox(inbox)"
            />
          </li>
        </ul>
      </ContentCard>

      <!-- Playground -->
      <ContentCard
        v-if="activeTab === 'playground' && selectedId"
        :title="t('TELETEL_ASSISTANT.PLAYGROUND.TITLE')"
      >
        <p class="mb-3 text-xs text-n-slate-10">
          {{ t('TELETEL_ASSISTANT.PLAYGROUND.HINT') }}
        </p>
        <div class="mb-3 flex max-h-96 flex-col gap-2 overflow-auto">
          <div
            v-for="(m, idx) in chat"
            :key="idx"
            class="rounded-lg px-3 py-2 text-sm"
            :class="
              m.role === 'user'
                ? 'self-end bg-n-slate-3 text-n-slate-12'
                : m.error
                  ? 'self-start bg-n-ruby-3 text-n-ruby-11'
                  : m.handoff
                    ? 'self-start bg-n-amber-3 text-n-amber-11'
                    : 'self-start bg-n-teal-3 text-n-slate-12'
            "
          >
            <p class="whitespace-pre-line">{{ m.content }}</p>
            <p
              v-if="m.role === 'assistant' && !m.error"
              class="mt-1 text-xs text-n-slate-10"
            >
              {{ t('TELETEL_ASSISTANT.PLAYGROUND.CONFIDENCE') }}:
              {{ Math.round((m.confidence || 0) * 100) }}%
              <template v-if="m.faqs && m.faqs.length">
                · {{ t('TELETEL_ASSISTANT.PLAYGROUND.USED') }}:
                {{ m.faqs.map(f => f.question).join(' | ') }}
              </template>
            </p>
            <p v-if="m.skippedTool" class="mt-1 text-xs text-n-amber-11">
              {{
                t('TELETEL_ASSISTANT.PLAYGROUND.TOOL_SKIPPED', {
                  tool: m.skippedTool,
                })
              }}
            </p>
          </div>
        </div>
        <form class="flex gap-2" @submit.prevent="ask">
          <input
            v-model="question"
            type="text"
            :placeholder="t('TELETEL_ASSISTANT.PLAYGROUND.PLACEHOLDER')"
            class="mb-0 h-9 flex-1 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
          />
          <Button
            type="submit"
            size="sm"
            :label="t('TELETEL_ASSISTANT.PLAYGROUND.ASK')"
            :is-loading="asking"
          />
        </form>
      </ContentCard>

      <!-- Performance -->
      <template v-if="activeTab === 'performance' && selectedId">
        <ContentCard
          :title="t('TELETEL_ASSISTANT.PERFORMANCE.TITLE')"
          :description="
            t('TELETEL_ASSISTANT.PERFORMANCE.HINT', {
              days: performance ? performance.days : 30,
            })
          "
        >
          <p
            v-if="performanceLoading && !performance"
            class="text-sm text-n-slate-11"
          >
            {{ t('TELETEL_ASSISTANT.LOADING') }}
          </p>
          <template v-else-if="performance">
            <p
              v-if="!performance.conversations"
              class="text-sm text-n-slate-11"
            >
              {{ t('TELETEL_ASSISTANT.PERFORMANCE.EMPTY') }}
            </p>
            <template v-else>
              <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div
                  v-for="tile in PERFORMANCE_TILES"
                  :key="tile"
                  class="rounded-lg border border-n-weak p-3"
                >
                  <p class="text-xs text-n-slate-10">
                    {{
                      t(`TELETEL_ASSISTANT.PERFORMANCE.${tile.toUpperCase()}`)
                    }}
                  </p>
                  <p class="text-xl font-medium text-n-slate-12">
                    {{ formatCredits(performance[tile]) }}
                  </p>
                </div>
              </div>
              <p
                v-if="performance.handoff_rate !== null"
                class="mt-3 text-sm text-n-slate-11"
              >
                {{
                  t('TELETEL_ASSISTANT.PERFORMANCE.HANDOFF_RATE', {
                    pct: performance.handoff_rate,
                  })
                }}
              </p>
            </template>
          </template>
        </ContentCard>

        <ContentCard
          v-if="performance && performance.conversations"
          :title="t('TELETEL_ASSISTANT.PERFORMANCE.UNANSWERED_TITLE')"
          :description="t('TELETEL_ASSISTANT.PERFORMANCE.UNANSWERED_HINT')"
        >
          <p
            v-if="!performance.unanswered.length"
            class="text-sm text-n-slate-11"
          >
            {{ t('TELETEL_ASSISTANT.PERFORMANCE.UNANSWERED_EMPTY') }}
          </p>
          <ul v-else class="divide-y divide-n-weak">
            <li
              v-for="(row, idx) in performance.unanswered"
              :key="idx"
              class="flex flex-col gap-1 py-2"
            >
              <p class="whitespace-pre-line text-sm text-n-slate-12">
                {{
                  row.question || t('TELETEL_ASSISTANT.PERFORMANCE.UNREADABLE')
                }}
              </p>
              <p class="flex flex-wrap gap-2 text-xs text-n-slate-10">
                <router-link
                  :to="conversationPath(row.conversation_id)"
                  class="text-n-blue-11 hover:underline"
                >
                  {{
                    t('TELETEL_ASSISTANT.PERFORMANCE.CONVERSATION', {
                      id: row.conversation_id,
                    })
                  }}
                </router-link>
                <span>{{ formatUpdated(row.at) }}</span>
              </p>
            </li>
          </ul>
        </ContentCard>
      </template>

      <!-- Sources -->
      <ContentCard
        v-if="activeTab === 'documents' && selectedId"
        :title="t('TELETEL_ASSISTANT.DOCUMENTS.TITLE')"
      >
        <p class="mb-3 text-xs text-n-slate-10">
          {{ t('TELETEL_ASSISTANT.DOCUMENTS.HINT') }}
        </p>
        <form class="mb-4 grid gap-2" @submit.prevent="addDocument">
          <input
            v-model="docDraft.name"
            type="text"
            :placeholder="t('TELETEL_ASSISTANT.DOCUMENTS.NAME')"
            class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
          />
          <input
            v-model="docDraft.url"
            type="url"
            dir="ltr"
            :placeholder="t('TELETEL_ASSISTANT.DOCUMENTS.URL')"
            class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
          />
          <textarea
            v-model="docDraft.text"
            rows="4"
            :placeholder="t('TELETEL_ASSISTANT.DOCUMENTS.TEXT')"
            class="mb-0 rounded-lg border border-n-weak bg-teletel-panel px-2 py-1 text-sm"
          />
          <!-- URL-only switches: follow same-site links, and re-read weekly. -->
          <div
            v-if="docDraft.url.trim()"
            class="flex flex-wrap gap-4 text-sm text-n-slate-12"
          >
            <label class="flex items-start gap-2">
              <input v-model="docDraft.crawl" type="checkbox" class="mt-1" />
              <span class="flex flex-col">
                {{ t('TELETEL_ASSISTANT.DOCUMENTS.CRAWL') }}
                <span class="text-xs text-n-slate-10">
                  {{ t('TELETEL_ASSISTANT.DOCUMENTS.CRAWL_HINT') }}
                </span>
              </span>
            </label>
            <label class="flex items-start gap-2">
              <input
                v-model="docDraft.auto_sync"
                type="checkbox"
                class="mt-1"
              />
              <span class="flex flex-col">
                {{ t('TELETEL_ASSISTANT.DOCUMENTS.AUTO_SYNC') }}
                <span class="text-xs text-n-slate-10">
                  {{ t('TELETEL_ASSISTANT.DOCUMENTS.AUTO_SYNC_HINT') }}
                </span>
              </span>
            </label>
          </div>
          <div>
            <Button
              type="submit"
              size="sm"
              :label="t('TELETEL_ASSISTANT.DOCUMENTS.ADD')"
              :is-loading="docSaving"
            />
          </div>
        </form>
        <p v-if="!documents.length" class="text-sm text-n-slate-11">
          {{ t('TELETEL_ASSISTANT.DOCUMENTS.EMPTY') }}
        </p>
        <div
          v-if="documents.length"
          class="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-xs"
        >
          <label class="flex items-center gap-2 text-n-slate-12">
            <input
              type="checkbox"
              :checked="selectedDocIds.length === documents.length"
              @change="toggleAllDocs"
            />
            {{ t('TELETEL_ASSISTANT.DOCUMENTS.SELECT_ALL') }}
          </label>
          <span class="text-n-slate-10">
            {{
              t('TELETEL_ASSISTANT.DOCUMENTS.SELECTED', {
                count: selectedDocIds.length,
              })
            }}
          </span>
          <Button
            size="xs"
            variant="outline"
            :disabled="!selectedDocIds.length"
            :label="t('TELETEL_ASSISTANT.DOCUMENTS.BULK_REINGEST')"
            @click="bulkDocuments('reingest')"
          />
          <Button
            size="xs"
            variant="outline"
            color="ruby"
            :disabled="!selectedDocIds.length"
            :label="t('TELETEL_ASSISTANT.DOCUMENTS.BULK_DELETE')"
            @click="bulkDocuments('delete')"
          />
        </div>
        <ul class="divide-y divide-n-weak">
          <li v-for="doc in documents" :key="doc.id" class="py-3">
            <div class="flex flex-wrap items-center gap-2">
              <input
                type="checkbox"
                :checked="selectedDocIds.includes(doc.id)"
                @change="toggleDoc(doc.id)"
              />
              <span class="font-medium text-n-slate-12">{{ doc.name }}</span>
              <span
                v-if="doc.crawl"
                class="rounded-full bg-n-blue-3 px-2 py-0.5 text-xs text-n-blue-11"
              >
                {{
                  t('TELETEL_ASSISTANT.DOCUMENTS.CRAWLED', {
                    pages: doc.crawled_pages || 0,
                  })
                }}
              </span>
              <span
                v-if="doc.auto_sync"
                class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
              >
                {{ t('TELETEL_ASSISTANT.DOCUMENTS.AUTO_SYNC_BADGE') }}
              </span>
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="
                  doc.sync_status === 'failed'
                    ? 'bg-n-ruby-3 text-n-ruby-11'
                    : doc.sync_status === 'synced'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                "
              >
                {{
                  t(
                    `TELETEL_ASSISTANT.DOCUMENTS.STATUS.${doc.sync_status || 'syncing'}`
                  )
                }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{
                  t('TELETEL_ASSISTANT.DOCUMENTS.COUNTS', {
                    generated: doc.generated || 0,
                    pending: doc.pending_faqs,
                    approved: doc.approved_faqs,
                  })
                }}
              </span>
            </div>
            <p
              v-if="doc.external_link"
              dir="ltr"
              class="truncate text-xs text-n-slate-10"
            >
              {{ doc.external_link }}
            </p>
            <p v-if="doc.last_error" class="text-xs text-n-ruby-11">
              {{ doc.last_error }}
            </p>
            <div class="mt-1 flex flex-wrap gap-2">
              <Button
                v-if="doc.pending_faqs > 0"
                size="xs"
                variant="outline"
                :label="t('TELETEL_ASSISTANT.DOCUMENTS.APPROVE_SOURCE')"
                @click="approveAll(doc.id)"
              />
              <Button
                size="xs"
                variant="ghost"
                :label="t('TELETEL_ASSISTANT.DOCUMENTS.REGENERATE')"
                @click="reingestDocument(doc)"
              />
              <Button
                size="xs"
                variant="ghost"
                color="ruby"
                :label="t('TELETEL_ASSISTANT.DOCUMENTS.DELETE')"
                @click="deleteDocument(doc)"
              />
            </div>
          </li>
        </ul>
      </ContentCard>

      <!-- Tools -->
      <ContentCard
        v-if="activeTab === 'tools'"
        :title="t('TELETEL_ASSISTANT.TOOLS.TITLE')"
      >
        <p class="mb-3 text-xs text-n-slate-10">
          {{ t('TELETEL_ASSISTANT.TOOLS.HINT') }}
        </p>
        <form class="mb-5 grid gap-2" @submit.prevent="saveTool">
          <input
            v-model="toolDraft.title"
            type="text"
            required
            :placeholder="t('TELETEL_ASSISTANT.TOOLS.NAME')"
            class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
          />
          <input
            v-model="toolDraft.description"
            type="text"
            :placeholder="t('TELETEL_ASSISTANT.TOOLS.DESCRIPTION')"
            class="mb-0 h-9 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
          />
          <div class="flex gap-2">
            <select
              v-model="toolDraft.http_method"
              class="mb-0 h-9 w-24 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            >
              <option value="GET">{{ 'GET' }}</option>
              <option value="POST">{{ 'POST' }}</option>
            </select>
            <input
              v-model="toolDraft.endpoint_url"
              type="url"
              dir="ltr"
              required
              :placeholder="t('TELETEL_ASSISTANT.TOOLS.URL')"
              class="mb-0 h-9 flex-1 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            />
          </div>
          <textarea
            v-model="toolDraft.parametersText"
            rows="3"
            dir="ltr"
            :placeholder="t('TELETEL_ASSISTANT.TOOLS.PARAMETERS')"
            class="mb-0 rounded-lg border border-n-weak bg-teletel-panel px-2 py-1 text-sm"
          />
          <textarea
            v-if="toolDraft.http_method === 'POST'"
            v-model="toolDraft.request_template"
            rows="2"
            dir="ltr"
            :placeholder="t('TELETEL_ASSISTANT.TOOLS.REQUEST_TEMPLATE')"
            class="mb-0 rounded-lg border border-n-weak bg-teletel-panel px-2 py-1 text-sm"
          />
          <textarea
            v-model="toolDraft.response_template"
            rows="2"
            dir="ltr"
            :placeholder="t('TELETEL_ASSISTANT.TOOLS.RESPONSE_TEMPLATE')"
            class="mb-0 rounded-lg border border-n-weak bg-teletel-panel px-2 py-1 text-sm"
          />
          <div class="flex flex-wrap items-center gap-2">
            <select
              v-model="toolDraft.auth_type"
              class="mb-0 h-9 w-32 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            >
              <option value="none">
                {{ t('TELETEL_ASSISTANT.TOOLS.AUTH_NONE') }}
              </option>
              <option value="bearer">
                {{ t('TELETEL_ASSISTANT.TOOLS.AUTH_BEARER') }}
              </option>
            </select>
            <input
              v-if="toolDraft.auth_type === 'bearer'"
              v-model="toolDraft.auth_token"
              type="password"
              dir="ltr"
              autocomplete="off"
              :placeholder="t('TELETEL_ASSISTANT.TOOLS.AUTH_TOKEN')"
              class="mb-0 h-9 flex-1 rounded-lg border border-n-weak bg-teletel-panel px-2 text-sm"
            />
          </div>
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            <input v-model="toolDraft.enabled" type="checkbox" class="mb-0" />
            {{ t('TELETEL_ASSISTANT.TOOLS.ENABLED') }}
          </label>
          <div class="flex gap-2">
            <Button
              type="submit"
              size="sm"
              :label="t('TELETEL_ASSISTANT.TOOLS.SAVE')"
              :is-loading="toolSaving"
            />
            <Button
              v-if="toolDraft.id"
              size="sm"
              variant="ghost"
              :label="t('TELETEL_ASSISTANT.TOOLS.CANCEL')"
              @click="resetTool"
            />
          </div>
        </form>

        <p v-if="!tools.length" class="text-sm text-n-slate-11">
          {{ t('TELETEL_ASSISTANT.TOOLS.EMPTY') }}
        </p>
        <ul class="divide-y divide-n-weak">
          <li v-for="tool in tools" :key="tool.id" class="py-3">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-medium text-n-slate-12">{{ tool.title }}</span>
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="
                  tool.enabled
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{
                  tool.enabled
                    ? t('TELETEL_ASSISTANT.TOOLS.ON')
                    : t('TELETEL_ASSISTANT.TOOLS.OFF')
                }}
              </span>
              <span class="text-xs text-n-slate-10" dir="ltr">
                {{ tool.http_method }} {{ tool.endpoint_url }}
              </span>
              <span v-if="tool.auth_configured" class="text-xs text-n-slate-10">
                {{ t('TELETEL_ASSISTANT.TOOLS.AUTH_SET') }}
              </span>
            </div>
            <p
              v-if="toolResult.id === tool.id && toolResult.text"
              class="mt-1 text-xs"
              :class="toolResult.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
            >
              {{ toolResult.text }}
            </p>
            <div
              v-if="tool.parameters.length"
              class="mt-2 flex flex-wrap items-center gap-2"
            >
              <span class="text-xs text-n-slate-10">
                {{ t('TELETEL_ASSISTANT.TOOLS.TEST_VALUES') }}
              </span>
              <input
                v-for="param in tool.parameters"
                :key="param.name"
                v-model="toolTestValues[tool.id][param.name]"
                type="text"
                dir="auto"
                :placeholder="param.name"
                :aria-label="param.name"
                class="mb-0 h-8 w-40 rounded-lg border border-n-weak bg-teletel-panel px-2 text-xs"
              />
            </div>
            <div class="mt-1 flex flex-wrap gap-2">
              <Button
                size="xs"
                variant="outline"
                :label="t('TELETEL_ASSISTANT.TOOLS.TEST')"
                :is-loading="toolTesting === tool.id"
                @click="testTool(tool)"
              />
              <Button
                size="xs"
                variant="ghost"
                :label="
                  tool.enabled
                    ? t('TELETEL_ASSISTANT.TOOLS.DISABLE')
                    : t('TELETEL_ASSISTANT.TOOLS.ENABLE')
                "
                @click="toggleTool(tool)"
              />
              <Button
                size="xs"
                variant="ghost"
                :label="t('TELETEL_ASSISTANT.TOOLS.EDIT')"
                @click="editTool(tool)"
              />
              <Button
                size="xs"
                variant="ghost"
                color="ruby"
                :label="t('TELETEL_ASSISTANT.TOOLS.DELETE')"
                @click="deleteTool(tool)"
              />
            </div>
          </li>
        </ul>
      </ContentCard>

      <!-- Usage: what this company has spent on AI this month -->
      <template v-if="activeTab === 'usage'">
        <ContentCard
          :title="t('TELETEL_ASSISTANT.USAGE.TITLE')"
          :description="t('TELETEL_ASSISTANT.USAGE.DESCRIPTION')"
        >
          <p v-if="usageLoading && !usage" class="text-sm text-n-slate-11">
            {{ t('TELETEL_ASSISTANT.LOADING') }}
          </p>

          <template v-else-if="usage">
            <p
              v-if="!usage.entitlement.allowed"
              class="mb-3 rounded-lg border border-n-amber-7 bg-n-amber-2 px-3 py-2 text-sm text-n-amber-12"
            >
              {{
                usage.entitlement.denial_reason === 'quota_exhausted'
                  ? t('TELETEL_ASSISTANT.USAGE.DENIED_QUOTA')
                  : t('TELETEL_ASSISTANT.USAGE.DENIED')
              }}
            </p>

            <div class="grid gap-3 sm:grid-cols-3">
              <div class="rounded-lg border border-n-weak p-3">
                <p class="text-xs text-n-slate-10">
                  {{ t('TELETEL_ASSISTANT.USAGE.USED') }}
                </p>
                <p class="text-xl font-medium text-n-slate-12">
                  {{ formatCredits(usage.entitlement.credits_used) }}
                  <span class="text-xs font-normal text-n-slate-10">
                    {{ t('TELETEL_ASSISTANT.USAGE.UNIT') }}
                  </span>
                </p>
              </div>
              <div class="rounded-lg border border-n-weak p-3">
                <p class="text-xs text-n-slate-10">
                  {{ t('TELETEL_ASSISTANT.USAGE.QUOTA') }}
                </p>
                <p class="text-xl font-medium text-n-slate-12">
                  {{
                    usage.entitlement.credits_quota
                      ? formatCredits(usage.entitlement.credits_quota)
                      : t('TELETEL_ASSISTANT.USAGE.UNLIMITED')
                  }}
                </p>
              </div>
              <div class="rounded-lg border border-n-weak p-3">
                <p class="text-xs text-n-slate-10">
                  {{ t('TELETEL_ASSISTANT.USAGE.REMAINING') }}
                </p>
                <p class="text-xl font-medium text-n-slate-12">
                  {{
                    usage.entitlement.credits_remaining === null
                      ? '\u2014'
                      : formatCredits(usage.entitlement.credits_remaining)
                  }}
                </p>
              </div>
            </div>

            <div v-if="usage.entitlement.usage_pct !== null" class="mt-3">
              <div class="h-2 w-full overflow-hidden rounded-full bg-n-slate-4">
                <div
                  class="h-full rounded-full"
                  :class="usageBarPct >= 90 ? 'bg-n-ruby-9' : 'bg-n-brand'"
                  :style="{ width: `${usageBarPct}%` }"
                />
              </div>
              <p class="mt-1 text-xs text-n-slate-10">
                {{
                  t('TELETEL_ASSISTANT.USAGE.PCT_OF_QUOTA', {
                    pct: usage.entitlement.usage_pct,
                  })
                }}
              </p>
            </div>

            <dl class="mt-4 grid gap-2 text-sm sm:grid-cols-2">
              <div class="flex gap-2">
                <dt class="text-n-slate-10">
                  {{ t('TELETEL_ASSISTANT.USAGE.MODE') }}
                </dt>
                <dd class="text-n-slate-12">
                  {{
                    t(
                      `TELETEL_ASSISTANT.USAGE.MODE_${usage.entitlement.mode.toUpperCase()}`
                    )
                  }}
                </dd>
              </div>
            </dl>

            <p
              v-if="
                usage.entitlement.credits_quota &&
                usage.entitlement.alert_threshold_pct
              "
              class="mt-2 text-xs text-n-slate-10"
            >
              {{
                t('TELETEL_ASSISTANT.USAGE.ALERT_AT', {
                  pct: usage.entitlement.alert_threshold_pct,
                })
              }}
              <span class="block mt-1 text-n-slate-10">
                {{ t('TELETEL_ASSISTANT.USAGE.RESERVE_NOTE') }}
              </span>
            </p>
          </template>
        </ContentCard>

        <ContentCard
          v-if="usage"
          :title="t('TELETEL_ASSISTANT.USAGE.BY_FEATURE')"
        >
          <p v-if="!usage.by_feature.length" class="text-sm text-n-slate-11">
            {{ t('TELETEL_ASSISTANT.USAGE.EMPTY') }}
          </p>
          <table v-else class="w-full text-sm">
            <thead class="text-xs text-n-slate-10">
              <tr>
                <th class="py-1 text-start">
                  {{ t('TELETEL_ASSISTANT.USAGE.FEATURE') }}
                </th>
                <th class="py-1 text-start">
                  {{ t('TELETEL_ASSISTANT.USAGE.CREDITS') }}
                </th>
                <th class="py-1 text-start">
                  {{ t('TELETEL_ASSISTANT.USAGE.CALLS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in usage.by_feature"
                :key="row.feature"
                class="border-t border-n-weak"
              >
                <td class="py-1.5 text-n-slate-12">
                  {{ featureLabel(row.feature) }}
                </td>
                <td class="py-1.5 text-n-slate-12">
                  {{ formatCredits(row.credits) }}
                </td>
                <td class="py-1.5 text-n-slate-11">
                  {{ formatCredits(row.calls) }}
                </td>
              </tr>
            </tbody>
          </table>
        </ContentCard>

        <ContentCard v-if="usage" :title="t('TELETEL_ASSISTANT.USAGE.TREND')">
          <div class="flex h-28 items-end gap-3">
            <div
              v-for="month in usage.by_month"
              :key="month.period"
              class="flex flex-1 flex-col items-center gap-1"
            >
              <span class="text-[10px] text-n-slate-10">
                {{ formatCredits(month.credits) }}
              </span>
              <div
                class="w-full rounded-t bg-n-brand/70"
                :style="{ height: monthBarHeight(month) }"
              />
              <span class="text-[10px] text-n-slate-10">
                {{ month.period }}
              </span>
            </div>
          </div>
        </ContentCard>
      </template>
    </template>
  </div>
</template>
